import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  ChevronRight,
  Phone,
  Mail,
  ShieldCheck,
  CheckCircle2,
  ChevronDown,
  ArrowRight,
  Clock,
  Award,
  Sparkles,
  Layers,
  Home as HomeIcon,
  ExternalLink
} from "lucide-react";
import ContactForm from "../components/ContactForm";
import { COMPANY_INFO } from "../data";

export default function LocalAreaPage({ page }) {
  const [openFaqIndex, setOpenFaqIndex] = useState(null);

  if (!page) return null;

  const toggleFaq = (index) => {
    setOpenFaqIndex(openFaqIndex === index ? null : index);
  };

  return (
    <div className="bg-primary-50/30">
      <Helmet>
        <title>{page.seo.title_tag}</title>
        <meta name="description" content={page.seo.meta_description} />
        <meta property="og:title" content={page.seo.title_tag} />
        <meta property="og:description" content={page.seo.meta_description} />
        {page.schema_markup && (
          <script type="application/ld+json">
            {JSON.stringify(page.schema_markup)}
          </script>
        )}
      </Helmet>

      {/* Hero / Header Section */}
      <section className="relative overflow-hidden bg-primary-950 py-16 sm:py-24 text-white">
        {/* Subtle decorative background glow */}
        <div className="absolute -top-40 -right-40 h-96 w-96 rounded-full bg-brand-500/10 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-40 -left-40 h-96 w-96 rounded-full bg-brand-400/10 blur-3xl pointer-events-none" />

        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          {/* Breadcrumbs */}
          <nav aria-label="Brødkrummer" className="flex flex-wrap items-center gap-2 text-xs text-primary-300 mb-6 sm:mb-8">
            <Link to="/" className="hover:text-white transition-colors flex items-center gap-1">
              <HomeIcon className="h-3.5 w-3.5" />
              <span>Forside</span>
            </Link>
            <ChevronRight className="h-3 w-3 text-primary-500" />
            <span className="text-brand-300 font-medium">{page.cityName}</span>
          </nav>

          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 rounded-full border border-brand-400/30 bg-brand-500/10 px-3.5 py-1 text-xs font-semibold text-brand-300 mb-4">
              <Sparkles className="h-3.5 w-3.5" />
              <span>Lokal tømrer i {page.cityName} & omegn</span>
            </div>

            <h1 className="font-display text-3xl font-bold tracking-tight text-white sm:text-4xl lg:text-5xl lg:leading-tight drop-shadow-xs">
              {page.h1}
            </h1>

            <p className="mt-6 text-base sm:text-lg leading-relaxed text-primary-200">
              {page.intro}
            </p>

            {/* CTAs */}
            <div className="mt-8 flex flex-wrap items-center gap-3.5">
              <a
                href="#kontakt"
                className="inline-flex items-center justify-center rounded-full bg-brand-400 px-6 py-3.5 text-sm font-bold text-primary-950 transition-colors duration-200 hover:bg-brand-300 shadow-md"
              >
                Få et uforpligtende tilbud
                <ArrowRight className="ml-2 h-4 w-4" />
              </a>
              <a
                href={`tel:${COMPANY_INFO.phoneRaw}`}
                className="inline-flex items-center justify-center gap-2 rounded-full border border-white/20 bg-white/10 px-6 py-3.5 text-sm font-semibold text-white transition-colors duration-200 hover:bg-white/20 hover:border-white/30 backdrop-blur-xs"
              >
                <Phone className="h-4 w-4 text-brand-300" />
                <span>Ring {COMPANY_INFO.phoneDisplay}</span>
              </a>
            </div>

            {/* Micro trust indicators */}
            <div className="mt-8 flex flex-wrap items-center gap-y-2 gap-x-6 text-xs text-primary-300">
              <div className="flex items-center gap-1.5">
                <CheckCircle2 className="h-4 w-4 text-brand-400" />
                <span>Over 20 års erfaring</span>
              </div>
              <div className="flex items-center gap-1.5">
                <CheckCircle2 className="h-4 w-4 text-brand-400" />
                <span>Én fast kontaktperson</span>
              </div>
              <div className="flex items-center gap-1.5">
                <CheckCircle2 className="h-4 w-4 text-brand-400" />
                <span>Dækket af Byg Garanti</span>
              </div>
              <a
                href="https://g.page/r/CS5RL2i4p0k0EBM/review"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 text-brand-300 hover:text-white transition-colors"
              >
                <svg className="h-3.5 w-3.5 shrink-0" viewBox="0 0 24 24" aria-hidden="true">
                  <path fill="#4285F4" d="M23.745 12.27c0-.7-.06-1.4-.19-2.07H12v4.51h6.6c-.29 1.52-1.14 2.82-2.4 3.68v3.05h3.88c2.27-2.09 3.665-5.17 3.665-9.17z" />
                  <path fill="#34A853" d="M12 24c3.24 0 5.95-1.08 7.93-2.91l-3.88-3.05c-1.08.72-2.45 1.16-4.05 1.16-3.12 0-5.77-2.1-6.72-4.93H1.25v3.15C3.26 21.36 7.33 24 12 24z" />
                  <path fill="#FBBC05" d="M5.28 14.27c-.25-.72-.38-1.49-.38-2.27s.14-1.55.38-2.27V6.58H1.25C.45 8.16 0 9.98 0 12s.45 3.84 1.25 5.42l4.03-3.15z" />
                  <path fill="#EA4335" d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.42-3.42C17.95 1.19 15.24 0 12 0 7.33 0 3.26 2.64 1.25 6.58l4.03 3.15c.95-2.83 3.6-4.98 6.72-4.98z" />
                </svg>
                <span>Anmeld på Google</span>
                <ExternalLink className="h-3 w-3 opacity-70" />
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content Area */}
      <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8 lg:py-16">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 items-start">
          {/* Main Editorial Text Column */}
          <div className="lg:col-span-8 space-y-8">
            {/* Unified Flowing Editorial Article */}
            <article className="rounded-2xl border border-primary-200/80 bg-white p-6 sm:p-8 lg:p-10 shadow-xs">
              {page.sections.map((sec, idx) => {
                // Skab en tættere og mere harmonisk overgang til de sidste afsnit
                const isLateSection = idx >= page.sections.length - 2;
                return (
                  <div
                    key={idx}
                    className={`${
                      idx === 0
                        ? ""
                        : isLateSection
                        ? "mt-4 pt-4 border-t border-primary-100/70"
                        : "mt-5 pt-5 border-t border-primary-100/90"
                    }`}
                  >
                    <h2 className="font-display text-xl sm:text-2xl font-bold tracking-tight text-primary-950 mb-2">
                      {sec.h2}
                    </h2>

                  {sec.paragraphs && sec.paragraphs.length > 0 && (
                    <div className="space-y-3 text-[15px] sm:text-base leading-relaxed text-primary-700">
                      {sec.paragraphs.map((p, pIdx) => (
                        <p key={pIdx}>{p}</p>
                      ))}
                    </div>
                  )}

                  {/* CTA knapper lige under første afsnit for hurtig konvertering */}
                  {idx === 0 && (
                    <div className="mt-4 flex flex-wrap items-center gap-2.5">
                      <a
                        href={`tel:${COMPANY_INFO.phoneRaw}`}
                        className="inline-flex items-center justify-center rounded-full border border-primary-300 bg-white hover:bg-primary-50 px-4 py-2 text-xs font-bold text-primary-950 transition-colors shadow-xs"
                      >
                        <Phone className="mr-1.5 h-3.5 w-3.5 text-brand-600" />
                        Ring {COMPANY_INFO.phoneDisplay}
                      </a>
                      <a
                        href={`mailto:${COMPANY_INFO.email}`}
                        className="inline-flex items-center justify-center rounded-full border border-primary-300 bg-white hover:bg-primary-50 px-4 py-2 text-xs font-bold text-primary-950 transition-colors shadow-xs"
                      >
                        <Mail className="mr-1.5 h-3.5 w-3.5 text-brand-600" />
                        Send mail
                      </a>
                      <a
                        href="#kontakt"
                        className="inline-flex items-center justify-center rounded-full bg-brand-600 hover:bg-brand-700 px-4 py-2 text-xs font-bold text-white transition-colors shadow-xs"
                      >
                        Få et tilbud
                        <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
                      </a>
                    </div>
                  )}

                  {sec.list && (
                    <div className="my-3">
                      <ul className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                        {sec.list.map((item, lIdx) => (
                          <li
                            key={lIdx}
                            className="flex items-start gap-2 text-sm sm:text-[15px] font-medium text-primary-800"
                          >
                            <CheckCircle2 className="h-4 w-4 text-brand-600 shrink-0 mt-0.5" />
                            <span>{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {sec.afterListParagraph && (
                    <p className="mt-3 text-[15px] sm:text-base leading-relaxed text-primary-700">
                      {sec.afterListParagraph}
                    </p>
                  )}

                  {/* Kun ét enkelt, velvalgt projektbillede midt i teksten */}
                  {idx === 1 && (
                    <figure className="my-4 overflow-hidden rounded-xl border border-primary-200/80 bg-primary-50/30 shadow-xs">
                      <img
                        src="https://assets.auxodev.dk/cdn-cgi/image/width=1000,quality=80,format=auto/assets/b61a68ca-a744-4f61-bada-84d3060a91a2/image00005-scaled.jpeg"
                        alt={`Tømrerarbejde udført af Sehested Tømrerfirma i ${page.cityName}`}
                        className="w-full h-52 sm:h-64 object-cover"
                        loading="lazy"
                      />
                      <figcaption className="py-2 px-3 text-xs text-center text-primary-600 font-medium bg-white border-t border-primary-100">
                        Solidt tømrerarbejde med sans for detaljen og holdbare materialer. – Sehested Tømrerfirma
                      </figcaption>
                    </figure>
                  )}

                  {/* Kompakt og diskret CTA uden store mellemrum */}
                  {idx === 2 && (
                    <div className="my-5 rounded-xl border border-primary-200/80 bg-primary-50/50 p-4">
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                        <p className="text-xs sm:text-sm font-semibold text-primary-950">
                          Skal vi se på dit byggeprojekt i {page.cityName}?
                        </p>
                        <div className="flex flex-wrap items-center gap-2">
                          <a
                            href={`tel:${COMPANY_INFO.phoneRaw}`}
                            className="inline-flex items-center justify-center rounded-full border border-primary-300 bg-white hover:bg-primary-100 px-3.5 py-1.5 text-xs font-bold text-primary-950 transition-colors shadow-2xs"
                          >
                            <Phone className="mr-1.5 h-3.5 w-3.5 text-brand-600" />
                            Ring {COMPANY_INFO.phoneDisplay}
                          </a>
                          <a
                            href={`mailto:${COMPANY_INFO.email}`}
                            className="inline-flex items-center justify-center rounded-full border border-primary-300 bg-white hover:bg-primary-100 px-3.5 py-1.5 text-xs font-bold text-primary-950 transition-colors shadow-2xs"
                          >
                            <Mail className="mr-1.5 h-3.5 w-3.5 text-brand-600" />
                            Send mail
                          </a>
                          <a
                            href="#kontakt"
                            className="inline-flex items-center justify-center rounded-full bg-brand-600 hover:bg-brand-700 px-3.5 py-1.5 text-xs font-bold text-white transition-colors shadow-2xs"
                          >
                            Få et tilbud
                            <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
                          </a>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Subsections if any */}
                  {sec.subsections && sec.subsections.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-primary-100 space-y-4">
                      {sec.subsections.map((sub, sIdx) => (
                        <div key={sIdx}>
                          <h3 className="font-display text-lg sm:text-xl font-bold text-primary-950 mb-2">
                            {sub.h3}
                          </h3>
                          <div className="space-y-2 text-[15px] sm:text-base leading-relaxed text-primary-700">
                            {sub.paragraphs.map((subP, subPIdx) => (
                              <p key={subPIdx}>{subP}</p>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
              })}
            </article>

            {/* Google Reviews Invitation Banner */}
            <div className="rounded-2xl border border-primary-200 bg-gradient-to-br from-white to-primary-50/50 p-6 sm:p-8 shadow-xs">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
                <div className="space-y-3 max-w-xl">
                  <div className="inline-flex items-center gap-2.5 rounded-full border border-primary-200 bg-white px-3 py-1 shadow-2xs">
                    <div className="h-4 w-4 shrink-0">
                      <svg className="h-full w-full" viewBox="0 0 24 24" aria-hidden="true">
                        <path fill="#4285F4" d="M23.745 12.27c0-.7-.06-1.4-.19-2.07H12v4.51h6.6c-.29 1.52-1.14 2.82-2.4 3.68v3.05h3.88c2.27-2.09 3.665-5.17 3.665-9.17z" />
                        <path fill="#34A853" d="M12 24c3.24 0 5.95-1.08 7.93-2.91l-3.88-3.05c-1.08.72-2.45 1.16-4.05 1.16-3.12 0-5.77-2.1-6.72-4.93H1.25v3.15C3.26 21.36 7.33 24 12 24z" />
                        <path fill="#FBBC05" d="M5.28 14.27c-.25-.72-.38-1.49-.38-2.27s.14-1.55.38-2.27V6.58H1.25C.45 8.16 0 9.98 0 12s.45 3.84 1.25 5.42l4.03-3.15z" />
                        <path fill="#EA4335" d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.42-3.42C17.95 1.19 15.24 0 12 0 7.33 0 3.26 2.64 1.25 6.58l4.03 3.15c.95-2.83 3.6-4.98 6.72-4.98z" />
                      </svg>
                    </div>
                    <div>
                      <div className="flex items-center gap-1.5">
                        <span className="text-xs font-bold text-primary-950">Google Anmeldelser</span>
                      </div>
                      <p className="text-xs text-primary-500 font-medium">Sehested Tømrerfirma • Hasselager & Aarhus</p>
                    </div>
                  </div>

                  <div>
                    <h3 className="font-display text-xl sm:text-2xl font-bold text-primary-950">
                      Har vi hjulpet dig med et bygge- eller renoveringsprojekt i {page.cityName}?
                    </h3>
                    <p className="mt-2 text-sm text-primary-700 leading-relaxed">
                      Din tilbagemelding har stor betydning for vores arbejde. Har du fået udført tømrerarbejde af Søren Sehested i {page.cityName} eller omegn, sætter vi stor pris på, hvis du vil dele din oplevelse med en hurtig anmeldelse på Google.
                    </p>
                  </div>

                  <div className="flex flex-wrap items-center gap-y-1 gap-x-4 text-xs text-primary-600 pt-1">
                    <span className="flex items-center gap-1">
                      <CheckCircle2 className="h-3.5 w-3.5 text-brand-600" /> Tager under 1 minut
                    </span>
                    <span className="flex items-center gap-1">
                      <CheckCircle2 className="h-3.5 w-3.5 text-brand-600" /> Direkte på Google
                    </span>
                    <span className="flex items-center gap-1">
                      <CheckCircle2 className="h-3.5 w-3.5 text-brand-600" /> Hjælper lokale boligejere
                    </span>
                  </div>
                </div>

                <div className="shrink-0 flex flex-col sm:flex-row md:flex-col items-stretch sm:items-center md:items-stretch gap-2.5">
                  <a
                    href="https://g.page/r/CS5RL2i4p0k0EBM/review"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center justify-center gap-2.5 rounded-full bg-primary-950 hover:bg-brand-600 px-6 py-3.5 text-xs sm:text-sm font-bold text-white transition-all duration-200 shadow-sm group cursor-pointer"
                  >
                    <svg className="h-4 w-4 shrink-0" viewBox="0 0 24 24" aria-hidden="true">
                      <path fill="#4285F4" d="M23.745 12.27c0-.7-.06-1.4-.19-2.07H12v4.51h6.6c-.29 1.52-1.14 2.82-2.4 3.68v3.05h3.88c2.27-2.09 3.665-5.17 3.665-9.17z" />
                      <path fill="#34A853" d="M12 24c3.24 0 5.95-1.08 7.93-2.91l-3.88-3.05c-1.08.72-2.45 1.16-4.05 1.16-3.12 0-5.77-2.1-6.72-4.93H1.25v3.15C3.26 21.36 7.33 24 12 24z" />
                      <path fill="#FBBC05" d="M5.28 14.27c-.25-.72-.38-1.49-.38-2.27s.14-1.55.38-2.27V6.58H1.25C.45 8.16 0 9.98 0 12s.45 3.84 1.25 5.42l4.03-3.15z" />
                      <path fill="#EA4335" d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.42-3.42C17.95 1.19 15.24 0 12 0 7.33 0 3.26 2.64 1.25 6.58l4.03 3.15c.95-2.83 3.6-4.98 6.72-4.98z" />
                    </svg>
                    <span>Giv en Google anmeldelse</span>
                    <ExternalLink className="h-3.5 w-3.5 opacity-70 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all" />
                  </a>
                  <p className="text-center text-[11px] text-primary-500">
                    Åbner direkte i Google Reviews
                  </p>
                </div>
              </div>
            </div>

            {/* Quick Contextual Services Banner with Internal Links */}
            <div className="rounded-2xl bg-gradient-to-br from-primary-900 to-primary-950 p-8 text-white shadow-sm border border-primary-800">
              <div className="flex items-center gap-2 text-brand-300 text-xs font-bold uppercase tracking-wider mb-3">
                <Layers className="h-4 w-4" />
                <span>Udforsk vores specialer</span>
              </div>
              <h3 className="font-display text-2xl font-bold text-white mb-2">
                Hvilken tømreropgave står du med i {page.cityName}?
              </h3>
              <p className="text-primary-200 text-sm leading-relaxed mb-6 max-w-2xl">
                Uanset om opgaven drejer sig om en komplet ombygning, nyt tag, træterrasse eller udskiftning af døre og vinduer, kan du læse mere om vores faglige ydelser her:
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2.5">
                {[
                  { title: "Totalrenovering", path: "/totalrenovering" },
                  { title: "Ombygning", path: "/ombygning" },
                  { title: "Totalentreprise", path: "/totalentreprise" },
                  { title: "Tilbygninger", path: "/ydelser/tilbygninger" },
                  { title: "Træterrasser", path: "/ydelser/traeterrasser" },
                  { title: "Døre & vinduer", path: "/ydelser/doere-og-vinduer" },
                  { title: "Tagarbejde", path: "/ydelser/tagarbejde" },
                  { title: "Tidligere projekter", path: "/tidligere-projekter" },
                  { title: "Horn Vinduesfabrik", path: "/tidligere-projekter/horn-vinduesfabrik" }
                ].map((item, i) => (
                  <Link
                    key={i}
                    to={item.path}
                    className="flex items-center justify-between rounded-lg bg-white/10 hover:bg-white/15 px-3.5 py-2.5 text-xs font-semibold text-white transition-colors border border-white/10"
                  >
                    <span>{item.title}</span>
                    <ChevronRight className="h-3.5 w-3.5 text-brand-300 shrink-0" />
                  </Link>
                ))}
              </div>
            </div>

            {/* FAQ Accordion Section */}
            {page.faq && page.faq.length > 0 && (
              <section className="rounded-2xl border border-primary-200 bg-white p-6 sm:p-9 shadow-xs">
                <div className="mb-6">
                  <div className="inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-brand-700 mb-2">
                    <CheckCircle2 className="h-4 w-4" />
                    <span>Ofte stillede spørgsmål</span>
                  </div>
                  <h3 className="font-display text-2xl font-bold tracking-tight text-primary-950 sm:text-3xl">
                    Spørgsmål og svar om tømrerarbejde i {page.cityName}
                  </h3>
                </div>

                <div className="divide-y divide-primary-100">
                  {page.faq.map((item, idx) => {
                    const isOpen = openFaqIndex === idx;
                    return (
                      <div key={idx} className="py-4 first:pt-0 last:pb-0">
                        <button
                          type="button"
                          onClick={() => toggleFaq(idx)}
                          className="flex w-full items-start justify-between text-left gap-4 group cursor-pointer"
                          aria-expanded={isOpen}
                        >
                          <h4 className="font-display text-base font-bold text-primary-950 group-hover:text-brand-700 transition-colors">
                            {item.question}
                          </h4>
                          <span className={`rounded-full p-1 border border-primary-200 transition-transform duration-200 shrink-0 ${isOpen ? "rotate-180 bg-brand-50 text-brand-700" : "text-primary-500"}`}>
                            <ChevronDown className="h-4 w-4" />
                          </span>
                        </button>
                        {isOpen && (
                          <p className="mt-3 text-sm leading-relaxed text-primary-700 animate-fadeIn">
                            {item.answer}
                          </p>
                        )}
                      </div>
                    );
                  })}
                </div>
              </section>
            )}

            {/* Direct Contact Form section */}
            <div id="kontakt" className="scroll-mt-24">
              <ContactForm
                title={`Fortæl mig om din tømreropgave i ${page.cityName}`}
                subtitle="Beskriv kort din bolig, dine ønsker og projektets omfang, så vender jeg hurtigt tilbage med en faglig vurdering."
              />
            </div>
          </div>

          {/* Sticky Sidebar */}
          <aside className="lg:col-span-4 space-y-6 lg:sticky lg:top-28">
            {/* Quick Contact Box */}
            <div className="rounded-2xl border border-primary-200 bg-white p-6 shadow-xs">
              <div className="flex items-center gap-3 pb-4 border-b border-primary-100">
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-brand-50 text-brand-700 border border-brand-200/60 font-display font-bold text-lg">
                  SS
                </div>
                <div>
                  <h4 className="font-display font-bold text-primary-950">Søren Sehested</h4>
                  <p className="text-xs text-primary-500">Tømrermester & Indehaver</p>
                </div>
              </div>

              <div className="mt-5 space-y-4 text-sm">
                <p className="text-primary-700 leading-relaxed text-xs">
                  Hos Sehested Tømrerfirma er det mig personligt, du taler med, og mig, der står med værktøjet i {page.cityName}.
                </p>

                <div className="space-y-2 pt-2">
                  <a
                    href={`tel:${COMPANY_INFO.phoneRaw}`}
                    className="flex items-center justify-center gap-2.5 w-full rounded-full bg-brand-400 hover:bg-brand-300 px-4 py-3 text-xs font-bold uppercase tracking-wider text-primary-950 transition-colors shadow-xs"
                  >
                    <Phone className="h-4 w-4" />
                    <span>Ring {COMPANY_INFO.phoneDisplay}</span>
                  </a>
                  <a
                    href={`mailto:${COMPANY_INFO.email}`}
                    className="flex items-center justify-center gap-2.5 w-full rounded-full border border-primary-200 hover:bg-primary-50 px-4 py-3 text-xs font-semibold text-primary-800 transition-colors"
                  >
                    <Mail className="h-4 w-4 text-primary-500" />
                    <span>{COMPANY_INFO.email}</span>
                  </a>
                </div>
              </div>

              <div className="mt-6 pt-4 border-t border-primary-100 space-y-2.5 text-xs text-primary-600">
                <div className="flex items-center gap-2">
                  <Award className="h-4 w-4 text-brand-600 shrink-0" />
                  <span>20+ års håndværkererfaring</span>
                </div>
                <div className="flex items-center gap-2">
                  <ShieldCheck className="h-4 w-4 text-brand-600 shrink-0" />
                  <span>Medlem af Byg Garanti & DI Byggeri</span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="h-4 w-4 text-brand-600 shrink-0" />
                  <span>Base i Hasselager – hurtig udkørsel til {page.cityName}</span>
                </div>
              </div>
            </div>

            {/* Google Review Callout */}
            <div className="rounded-2xl border border-primary-200 bg-white p-6 shadow-xs space-y-4 text-center">
              <div className="flex items-center justify-center gap-3">
                <div className="h-8 w-8 shrink-0 p-1.5 rounded-full bg-primary-50 border border-primary-200">
                  <svg className="h-full w-full" viewBox="0 0 24 24" aria-hidden="true">
                    <path fill="#4285F4" d="M23.745 12.27c0-.7-.06-1.4-.19-2.07H12v4.51h6.6c-.29 1.52-1.14 2.82-2.4 3.68v3.05h3.88c2.27-2.09 3.665-5.17 3.665-9.17z" />
                    <path fill="#34A853" d="M12 24c3.24 0 5.95-1.08 7.93-2.91l-3.88-3.05c-1.08.72-2.45 1.16-4.05 1.16-3.12 0-5.77-2.1-6.72-4.93H1.25v3.15C3.26 21.36 7.33 24 12 24z" />
                    <path fill="#FBBC05" d="M5.28 14.27c-.25-.72-.38-1.49-.38-2.27s.14-1.55.38-2.27V6.58H1.25C.45 8.16 0 9.98 0 12s.45 3.84 1.25 5.42l4.03-3.15z" />
                    <path fill="#EA4335" d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.42-3.42C17.95 1.19 15.24 0 12 0 7.33 0 3.26 2.64 1.25 6.58l4.03 3.15c.95-2.83 3.6-4.98 6.72-4.98z" />
                  </svg>
                </div>
                <div className="text-left">
                  <p className="text-xs font-bold text-primary-950">Google Anmeldelser</p>
                  <p className="text-[11px] text-primary-500 font-medium">Sehested Tømrerfirma</p>
                </div>
              </div>

              <p className="text-xs text-primary-600 leading-relaxed">
                Har du samarbejdet med Sehested Tømrerfirma i {page.cityName}? Vi værdsætter din bedømmelse højt.
              </p>

              <a
                href="https://g.page/r/CS5RL2i4p0k0EBM/review"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-1.5 w-full rounded-full border border-primary-300 bg-primary-50 hover:bg-brand-600 hover:text-white hover:border-brand-600 px-3.5 py-2.5 text-xs font-bold text-primary-900 transition-all shadow-xs group cursor-pointer"
              >
                <span>Giv en bedømmelse</span>
                <ExternalLink className="h-3 w-3 opacity-70 group-hover:opacity-100" />
              </a>
            </div>

            {/* Other Area Navigation Links */}
            <div className="rounded-2xl border border-primary-200 bg-white p-6 shadow-xs">
              <h4 className="font-display text-sm font-bold uppercase tracking-wider text-primary-900 mb-3">
                Lokalområder vi dækker
              </h4>
              <p className="text-xs text-primary-600 mb-4">
                Vi har base i Hasselager og løser opgaver i hele Aarhus Syd og omegn:
              </p>
              <div className="flex flex-wrap gap-1.5">
                {[
                  { name: "Hasselager", path: "/" },
                  { name: "Aarhus", path: "/" },
                  { name: "Højbjerg", path: "/toemrer-hoejbjerg" },
                  { name: "Viby", path: "/toemrer-viby" },
                  { name: "Mårslet", path: "/toemrer-maarslet" },
                  { name: "Skanderborg", path: "/toemrer-skanderborg" },
                  { name: "Odder", path: "/toemrer-odder" }
                ].map((loc, idx) => (
                  <Link
                    key={idx}
                    to={loc.path}
                    className={`inline-block px-3 py-1 text-xs rounded-md transition-colors ${
                      loc.name.toLowerCase() === page.cityName.toLowerCase()
                        ? "bg-brand-500 text-primary-950 font-bold border border-brand-600"
                        : "bg-primary-50 text-primary-800 hover:bg-brand-50 hover:text-brand-900 border border-primary-200/80"
                    }`}
                  >
                    {loc.name}
                  </Link>
                ))}
              </div>
            </div>

            {/* Byg Garanti badge */}
            <div className="rounded-2xl border border-primary-200/70 bg-primary-50/50 p-5 text-center">
              <div className="mx-auto h-12 w-32 flex items-center justify-center mb-3">
                <img
                  src={COMPANY_INFO.bygGarantiLogo}
                  alt="Byg Garanti"
                  className="h-full w-auto object-contain filter brightness-0 opacity-80"
                />
              </div>
              <p className="text-xs text-primary-600">
                Alt tømrerarbejde i {page.cityName} er omfattet af Byg Garanti, så du er sikret op til 150.000 kr.
              </p>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}
